/**
 * Derives the per-thread task indicators the sidebar rows render: the
 * status dot and the unread-completion dot.
 *
 * Lives outside the component so `ChatHistory` stays a component-only module
 * (the fast-refresh boundary) and so this stays unit-testable without rendering.
 */
import type { Task } from "types/Task";
import { shouldStopPolling } from "types/TaskRegistry";
import { getUnreadCompletionIndicator } from "types/Task";
import styles from "../ChatHistory.module.scss";

/** Per-thread task indicators rendered on a sidebar row. */
export interface ThreadTaskIndicator {
  /** Status-dot class, or null when no status dot should render. */
  dotClass: string | null;
  hasUnreadCompletion: boolean;
  unreadFailed: boolean;
}

/**
 * Indicators for every thread that has at least one non-dismissed task, in a
 * single pass over the cross-chat task list.
 *
 * Computed once for the whole sidebar rather than per row: each row previously
 * subscribed to the tasks query itself and re-scanned the entire list, so one
 * poll tick re-rendered every row and did O(rows x tasks) work. Rows now take
 * their indicator as a prop and hold no subscription.
 */
export function buildThreadTaskIndicators(
  tasks: Task[]
): Map<string, ThreadTaskIndicator> {
  const byThread = new Map<string, Task[]>();
  for (const task of tasks) {
    if (task.dismissed) continue;
    const existing = byThread.get(task.threadId);
    if (existing) {
      existing.push(task);
    } else {
      byThread.set(task.threadId, [task]);
    }
  }

  const indicators = new Map<string, ThreadTaskIndicator>();
  for (const [threadId, threadTasks] of byThread) {
    // Same helper the cross-chat clipboard dot uses, so the two surfaces cannot
    // disagree about what counts as an unread failure.
    const { hasUnread: hasUnreadCompletion, hasFailed: unreadFailed } =
      getUnreadCompletionIndicator(threadTasks);

    let dotClass: string | null = null;
    if (threadTasks.some((t) => !shouldStopPolling(t.status))) {
      dotClass = styles.taskDotWorking;
    } else {
      // Latest by createdAt, found in one scan rather than by copying and sorting.
      let latest = threadTasks[0];
      for (const task of threadTasks) {
        if (task.createdAt.localeCompare(latest.createdAt) > 0) latest = task;
      }
      if (latest.status === "failed" || latest.status === "cancelled") {
        dotClass = styles.taskDotFailed;
      } else if (latest.status === "completed") {
        dotClass = styles.taskDotCompleted;
      }
    }

    indicators.set(threadId, { dotClass, hasUnreadCompletion, unreadFailed });
  }
  return indicators;
}
